import { useCallback, useState } from 'react';
import bookingService from '../services/bookingService';
import { submitBookingLead } from '../services/leadCaptureService';
import authService from '../services/authService';
import { RUNTIME_CONFIG } from '../config/runtimeConfig';
import { addRecentBooking } from '../utils/bookingPreferences';
import { validateBookingPayload } from '../utils/payloadSchemas';

const getReadableError = (error, fallback = 'Something went wrong. Please try again.') => {
  if (!error) return fallback;

  if (typeof error === 'string') {
    return error;
  }

  if (typeof error?.message === 'string' && error.message.trim()) {
    return error.message;
  }

  if (typeof error?.error === 'string' && error.error.trim()) {
    return error.error;
  }

  if (typeof error?.message === 'object' && error.message) {
    if (typeof error.message.message === 'string' && error.message.message.trim()) {
      return error.message.message;
    }
    try {
      return JSON.stringify(error.message);
    } catch (serializationError) {
      return fallback;
    }
  }

  if (typeof error?.error === 'object' && error.error) {
    if (typeof error.error.message === 'string' && error.error.message.trim()) {
      return error.error.message;
    }
    try {
      return JSON.stringify(error.error);
    } catch (serializationError) {
      return fallback;
    }
  }

  return fallback;
};

const useBookingSubmission = ({
  validateCurrentStep,
  booking,
  formData,
  onNavigate,
  servicePage,
  selectedSlot,
  availabilityWindow,
  serviceInfo,
  finalStepNumber = 'single'
}) => {
  const [submitState, setSubmitState] = useState({ loading: false, error: '' });

  const getSelectedOrFormSlotWindow = useCallback(() => {
    if (selectedSlot) {
      return {
        slotStart: selectedSlot.start_time || selectedSlot.startTime,
        slotEnd: selectedSlot.end_time || selectedSlot.endTime
      };
    }

    if (!availabilityWindow) {
      return null;
    }

    return {
      slotStart: availabilityWindow.from,
      slotEnd: availabilityWindow.to
    };
  }, [selectedSlot, availabilityWindow]);

  const resolvePanditId = useCallback(() => {
    if (selectedSlot && (selectedSlot.actor_id || selectedSlot.actorId)) {
      return Number(selectedSlot.actor_id || selectedSlot.actorId);
    }

    if (serviceInfo?.panditId) {
      return Number(serviceInfo.panditId);
    }

    if (serviceInfo?.providerId) {
      return Number(serviceInfo.providerId);
    }

    return null;
  }, [selectedSlot, serviceInfo]);

  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();
    const validatorStep = finalStepNumber === 'single' ? 'single' : finalStepNumber;

    if (!validateCurrentStep(validatorStep)) {
      return;
    }

    const bookingValidation = validateBookingPayload(booking);
    if (!bookingValidation.valid) {
      setSubmitState({ loading: false, error: bookingValidation.errors.join(' ') });
      return;
    }

    setSubmitState({ loading: true, error: '' });

    try {
      const currentUser = authService.getCurrentUser();
      const userId = currentUser?.id ? Number(currentUser.id) : null;
      const slotWindow = getSelectedOrFormSlotWindow();
      const panditId = resolvePanditId();

      const fallbackLeadPayload = {
        source: 'booking-page',
        serviceTitle: booking?.service?.title || 'Service Booking',
        packageName: booking?.package?.name || 'Selected Package',
        totalPrice: Number(booking?.totalPrice || 0),
        eventDate: formData.eventDate,
        eventTime: formData.eventTime,
        customerName: `${formData.firstName || ''} ${formData.lastName || ''}`.trim(),
        customerPhone: formData.phone,
        customerEmail: formData.email,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        pincode: formData.pincode,
        notes: formData.specialRequirements || '',
        panditName: booking?.service?.selectedPandit?.name || (panditId ? `Pandit #${panditId}` : ''),
        panditPhone: booking?.service?.selectedPandit?.phone || '',
        payload: {
          booking,
          formData,
          slotWindow
        }
      };

      const submitAsLead = async () => {
        const leadResult = await submitBookingLead(fallbackLeadPayload, { openWhatsApp: false });
        if (onNavigate) {
          onNavigate('booking-confirmation', {
            bookingData: {
              ...formData,
              booking,
              leadId: leadResult?.lead?.id,
              leadCaptured: true,
              backendBooking: null,
              servicePage: booking?.servicePage || servicePage
            }
          });
        }
      };

      if (!userId && RUNTIME_CONFIG.LEAD_CAPTURE_ENABLED) {
        await submitAsLead();
        setSubmitState({ loading: false, error: '' });
        return;
      }

      if (!userId) {
        throw new Error('Please login before confirming your booking.');
      }

      if (!panditId) {
        throw new Error('Provider is not selected yet. Please choose an available slot first.');
      }

      if (!slotWindow?.slotStart || !slotWindow?.slotEnd) {
        throw new Error('Please choose a valid booking date/time.');
      }

      const requestPayload = {
        userId,
        panditId,
        slotStart: slotWindow.slotStart,
        slotEnd: slotWindow.slotEnd,
        notes: formData.specialRequirements || `${booking?.service?.title || 'Service'} booking request`
      };

      const createdBooking = await bookingService.createBooking(requestPayload);

      addRecentBooking({
        bookingId: createdBooking?.id,
        panditId,
        panditName: booking?.service?.selectedPandit?.name || `Pandit #${panditId}`,
        panditLocation: booking?.service?.selectedPandit?.location || '',
        serviceTitle: booking?.service?.title || 'Service',
        bookedAt: new Date().toISOString()
      });

      let successLead = null;
      if (RUNTIME_CONFIG.LEAD_CAPTURE_ENABLED) {
        try {
          successLead = await submitBookingLead(
            {
              ...fallbackLeadPayload,
              source: 'booking-page-success',
              payload: {
                booking,
                formData,
                slotWindow,
                backendBooking: createdBooking
              }
            },
            { openWhatsApp: false }
          );
        } catch (leadError) {
        }
      }

      if (onNavigate) {
        onNavigate('booking-confirmation', {
          bookingData: {
            ...formData,
            booking,
            backendBooking: createdBooking,
            leadId: successLead?.lead?.id || null,
            leadCaptured: Boolean(successLead?.lead),
            servicePage: booking?.servicePage || servicePage
          }
        });
      }

      setSubmitState({ loading: false, error: '' });
    } catch (error) {
      if (RUNTIME_CONFIG.LEAD_CAPTURE_ENABLED) {
        try {
          const leadResult = await submitBookingLead(
            {
              source: 'booking-page-fallback',
              serviceTitle: booking?.service?.title || 'Service Booking',
              packageName: booking?.package?.name || 'Selected Package',
              totalPrice: Number(booking?.totalPrice || 0),
              eventDate: formData.eventDate,
              eventTime: formData.eventTime,
              customerName: `${formData.firstName || ''} ${formData.lastName || ''}`.trim(),
              customerPhone: formData.phone,
              customerEmail: formData.email,
              address: formData.address,
              city: formData.city,
              state: formData.state,
              pincode: formData.pincode,
              notes: formData.specialRequirements || '',
              panditName: booking?.service?.selectedPandit?.name || '',
              panditPhone: booking?.service?.selectedPandit?.phone || '',
              payload: { booking, formData }
            },
            { openWhatsApp: false }
          );

          if (onNavigate) {
            onNavigate('booking-confirmation', {
              bookingData: {
                ...formData,
                booking,
                leadId: leadResult?.lead?.id,
                leadCaptured: true,
                backendBooking: null,
                servicePage: booking?.servicePage || servicePage
              }
            });
          }

          setSubmitState({ loading: false, error: '' });
          return;
        } catch (leadError) {
        }
      }

      setSubmitState({ loading: false, error: getReadableError(error, 'Booking failed. Please try again.') });
    }
  }, [
    validateCurrentStep,
    finalStepNumber,
    booking,
    formData,
    getSelectedOrFormSlotWindow,
    resolvePanditId,
    onNavigate,
    servicePage
  ]);

  return {
    submitState,
    handleSubmit
  };
};

export default useBookingSubmission;
